import { useCallback } from 'react'

import ConfirmationModal, { Props as ConfirmationModalProps } from 'components/ConfirmationModal'

import { uuidv4 } from 'utils/generic'
import { useAppContext } from 'modules/App/AppContext'

export type ModalConfirmationProps = Omit<ConfirmationModalProps, 'cancel'> & {
  cancel?: ConfirmationModalProps['cancel']
}

const id = uuidv4()

interface Props {
  stack?: string
}

const useModal = ({ stack }: Props = {}) => {
  const {
    setModal: setModalContext,
    setCloseModal: setCloseModalContext,
    resetModals,
    setModals,
    data,
  } = useAppContext()

  const setCloseModal = useCallback(
    (modalId?: string) => {
      setCloseModalContext(modalId ?? id)
    },
    [setCloseModalContext],
  )

  const setModal = useCallback(
    (data: JSX.Element | null, modalId?: string) => {
      if (!data) {
        setCloseModal(modalId)
        return
      }

      setModalContext({
        id: modalId ?? id,
        component: data,
        stack,
      })
    },
    [setCloseModal, setModalContext, stack],
  )

  const setConfirmationModal = useCallback(
    (data: ModalConfirmationProps | null, modalId?: string) => {
      if (!data) {
        setCloseModal(modalId)
        return
      }

      setModalContext({
        id: modalId ?? id,
        component: (
          <ConfirmationModal
            {...data}
            cancel={() => {
              if (data?.cancel) {
                data.cancel()
                return
              }
              setCloseModal(modalId)
            }}
          >
            {data?.children}
          </ConfirmationModal>
        ),
        stack,
      })
    },
    [setCloseModal, setModalContext, stack],
  )

  const setCloseStack = useCallback(
    (stack: string) => {
      setModals(data.modal.filter((item) => item.stack !== stack))
    },
    [setModals, data.modal],
  )

  return { setModal, setConfirmationModal, setCloseModal, resetModals, setCloseStack }
}

export default useModal
